import { z } from 'zod';

// Mapping Python ↔ TS (snake_case ↔ camelCase) :
//   block_id        ↔ blockId
//   char_start      ↔ charStart
//   char_end        ↔ charEnd
//   cell_range      ↔ cellRange
//   shape_id        ↔ shapeId
//   page_count      ↔ pageCount
//   rendered_html_object_key ↔ renderedHtmlObjectKey
//   parser_name     ↔ parserName
//   parser_version  ↔ parserVersion
//   source_doc_id   ↔ sourceDocId
//   section_path    ↔ sectionPath
//   table_type      ↔ tableType
const blockIdSchema = z.string().regex(/^b_[a-f0-9]{8}$/);
const cellRangeSchema = z.string().regex(/^[A-Z]+\d+(:[A-Z]+\d+)?$/);
const semverSchema = z.string().regex(/^\d+\.\d+\.\d+$/);

const charRangeSchema = {
  charStart: z.number().int().nonnegative(),
  charEnd: z.number().int().positive(),
} as const;

export const chunkAnchorPdfSchema = z.object({
  kind: z.literal('pdf'),
  page: z.number().int().positive(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional().nullable(),
}).strict();

export const chunkAnchorDocxHtmlSchema = z.object({
  kind: z.literal('docx_html'),
  blockId: blockIdSchema,
  ...charRangeSchema,
}).strict().refine((value) => value.charStart < value.charEnd, {
  message: 'charStart must be lower than charEnd',
  path: ['charEnd'],
});

export const chunkAnchorXlsxSchema = z.object({
  kind: z.literal('xlsx'),
  sheet: z.string().min(1),
  cellRange: cellRangeSchema,
}).strict();

export const chunkAnchorPptxSchema = z.object({
  kind: z.literal('pptx'),
  slide: z.number().int().positive(),
  shapeId: z.string().optional().nullable(),
}).strict();

export const chunkAnchorMarkdownSchema = z.object({
  kind: z.literal('markdown'),
  ...charRangeSchema,
}).strict().refine((value) => value.charStart < value.charEnd, {
  message: 'charStart must be lower than charEnd',
  path: ['charEnd'],
});

export const chunkAnchorSchema = z.discriminatedUnion('kind', [
  chunkAnchorPdfSchema,
  chunkAnchorDocxHtmlSchema,
  chunkAnchorXlsxSchema,
  chunkAnchorPptxSchema,
  chunkAnchorMarkdownSchema,
]);

const documentBlockTypeSchema = z.enum(['heading', 'paragraph', 'list_item', 'table', 'figure_caption', 'code']);

export const documentBlockSchema = z.object({
  id: blockIdSchema,
  type: documentBlockTypeSchema,
  text: z.string(),
  level: z.number().int().min(1).max(9).nullable().default(null),
  sectionPath: z.array(z.string()).default([]),
  anchor: chunkAnchorSchema,
  metadata: z.record(z.string(), z.unknown()).default({}),
}).strict().superRefine((value, ctx) => {
  if (value.type === 'heading' && value.level === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'level is required for heading blocks',
      path: ['level'],
    });
  }
  if (value.type !== 'heading' && value.level !== null) {
    ctx.addIssue({
      code: 'custom',
      message: 'level must be null for non-heading blocks',
      path: ['level'],
    });
  }
});

export const extractedTableBlockSchema = z.object({
  id: blockIdSchema,
  rows: z.array(z.array(z.string())),
  caption: z.string().nullable().default(null),
  tableType: z.enum(['error_codes', 'parts_list', 'maintenance_schedule', 'specs', 'generic']).default('generic'),
  anchor: chunkAnchorSchema,
  metadata: z.record(z.string(), z.unknown()).default({}),
}).strict();

export const documentIRSchema = z.object({
  sourceDocId: z.string(),
  mime: z.string(),
  blocks: z.array(documentBlockSchema).max(20_000),
  tables: z.array(extractedTableBlockSchema).default([]),
  pageCount: z.number().int().nullable().default(null),
  renderedHtmlObjectKey: z.string().nullable().default(null),
  parserName: z.string(),
  parserVersion: semverSchema,
}).strict();

export const parseInputSchema = z.object({
  docId: z.string(),
  mime: z.string(),
  sourcePath: z.string(),
  companyId: z.string().nullable().default(null),
}).strict();

export type ChunkAnchor = z.infer<typeof chunkAnchorSchema>;
export type ChunkAnchorPdf = z.infer<typeof chunkAnchorPdfSchema>;
export type ChunkAnchorDocxHtml = z.infer<typeof chunkAnchorDocxHtmlSchema>;
export type ChunkAnchorXlsx = z.infer<typeof chunkAnchorXlsxSchema>;
export type ChunkAnchorPptx = z.infer<typeof chunkAnchorPptxSchema>;
export type ChunkAnchorMarkdown = z.infer<typeof chunkAnchorMarkdownSchema>;
export type DocumentBlock = z.infer<typeof documentBlockSchema>;
export type ExtractedTableBlock = z.infer<typeof extractedTableBlockSchema>;
export type DocumentIR = z.infer<typeof documentIRSchema>;
export type ParseInput = z.infer<typeof parseInputSchema>;

export function isAnchorPdf(anchor: ChunkAnchor): anchor is ChunkAnchorPdf {
  return anchor.kind === 'pdf';
}

export function isAnchorDocxHtml(anchor: ChunkAnchor): anchor is ChunkAnchorDocxHtml {
  return anchor.kind === 'docx_html';
}

export function isAnchorXlsx(anchor: ChunkAnchor): anchor is ChunkAnchorXlsx {
  return anchor.kind === 'xlsx';
}

export function isAnchorPptx(anchor: ChunkAnchor): anchor is ChunkAnchorPptx {
  return anchor.kind === 'pptx';
}

export function isAnchorMarkdown(anchor: ChunkAnchor): anchor is ChunkAnchorMarkdown {
  return anchor.kind === 'markdown';
}
